import {
  Add,
  ChevronRight,
  ExpandMore,
  Refresh,
  Search,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view-pro';
import { useCreateType, useFieldTypes } from 'api/fieldEditor';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useNotifications } from 'components/notify';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatName } from 'utils/fields';

type TypeNode = {
  id: string;
  segment: string;
  fullType: string;
  description: string | null;
  exists: boolean;
  children: TypeNode[];
};

function buildTypeTree(
  types: { type: string; description: string | null }[],
): TypeNode[] {
  const nodeMap = new Map<string, TypeNode>();

  for (const t of types) {
    const segments = t.type.split('.');
    for (let i = 1; i <= segments.length; i++) {
      const id = segments.slice(0, i).join('.');
      if (!nodeMap.has(id)) {
        nodeMap.set(id, {
          id,
          segment: segments[i - 1],
          fullType: id,
          description: null,
          exists: false,
          children: [],
        });
      }
    }
    const node = nodeMap.get(t.type);
    if (node) {
      node.exists = true;
      node.description = t.description;
    }
  }

  const roots: TypeNode[] = [];
  for (const node of Array.from(nodeMap.values())) {
    const lastDot = node.id.lastIndexOf('.');
    if (lastDot === -1) {
      roots.push(node);
    } else {
      const parent = nodeMap.get(node.id.substring(0, lastDot));
      if (parent) parent.children.push(node);
      else roots.push(node);
    }
  }

  const sortRecursive = (nodes: TypeNode[]) => {
    nodes.sort((a, b) => a.segment.localeCompare(b.segment));
    nodes.forEach((n) => sortRecursive(n.children));
  };
  sortRecursive(roots);

  return roots;
}

function collectAllParentIds(nodes: TypeNode[]): string[] {
  const ids: string[] = [];
  const walk = (ns: TypeNode[]) => {
    for (const n of ns) {
      if (n.children.length > 0) {
        ids.push(n.id);
        walk(n.children);
      }
    }
  };
  walk(nodes);
  return ids;
}

export default function FieldEditorTypesPage() {
  const fieldTypes = useFieldTypes();
  const createType = useCreateType();
  const workspaceId = useWorkspaceId();
  const { showNotification } = useNotifications();
  const navigate = useNavigate();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeDescription, setNewTypeDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const filteredTypes = useMemo(() => {
    if (!fieldTypes.data) return [];
    if (!searchQuery.trim()) return fieldTypes.data;

    const query = searchQuery.toLowerCase();
    return fieldTypes.data.filter(
      (type) =>
        type.type.toLowerCase().includes(query) ||
        formatName(type.type).toLowerCase().includes(query) ||
        (type.description?.toLowerCase().includes(query) ?? false),
    );
  }, [fieldTypes.data, searchQuery]);

  const treeNodes = useMemo(
    () => buildTypeTree(filteredTypes),
    [filteredTypes],
  );

  const searchExpandedItems = useMemo(
    () => (searchQuery.trim() ? collectAllParentIds(treeNodes) : []),
    [searchQuery, treeNodes],
  );

  const effectiveExpandedItems = useMemo(
    () => Array.from(new Set([...expandedItems, ...searchExpandedItems])),
    [expandedItems, searchExpandedItems],
  );

  const handleCreateType = async () => {
    if (!newTypeName.trim()) {
      showNotification('Type name is required', { variant: 'error' });
      return;
    }

    setCreating(true);
    try {
      await createType(newTypeName.trim(), newTypeDescription.trim() || null);
      showNotification('Type created successfully', { variant: 'success' });
      setCreateDialogOpen(false);
      setNewTypeName('');
      setNewTypeDescription('');
      fieldTypes.mutate();
    } catch (error) {
      console.error('Failed to create type:', error);
      showNotification('Failed to create type', { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleRefresh = () => {
    fieldTypes.mutate();
  };

  const renderNode = (node: TypeNode): React.ReactNode => (
    <TreeItem
      key={node.id}
      itemId={node.id}
      label={
        <Box
          onClick={(e) => {
            if (!node.exists) return;
            e.stopPropagation();
            navigate(`/workspace/${workspaceId}/field-editor/${node.fullType}`);
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.75,
            pr: 1,
            cursor: node.exists ? 'pointer' : 'default',
            opacity: node.exists ? 1 : 0.65,
          }}
        >
          <Typography
            variant="body1"
            component="span"
            fontWeight={node.children.length > 0 ? 600 : 400}
          >
            {formatName(node.segment)}
          </Typography>
          <Chip
            label={node.fullType}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          {node.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              — {node.description}
            </Typography>
          )}
        </Box>
      }
    >
      {node.children.map(renderNode)}
    </TreeItem>
  );

  if (fieldTypes.error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load types. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Entity Types
        </Typography>
        <Stack direction="row" gap={1}>
          <IconButton onClick={handleRefresh} disabled={!fieldTypes.data}>
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Type
          </Button>
        </Stack>
      </Stack>

      <TextField
        fullWidth
        placeholder="Search types..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      {!fieldTypes.data ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      ) : treeNodes.length === 0 ? (
        <Alert severity="info">
          {searchQuery
            ? 'No types found matching your search.'
            : 'No types available. Create your first type to get started.'}
        </Alert>
      ) : (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            minHeight: 400,
          }}
        >
          <SimpleTreeView
            expandedItems={effectiveExpandedItems}
            onExpandedItemsChange={(_, items) => setExpandedItems(items)}
            slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
          >
            {treeNodes.map(renderNode)}
          </SimpleTreeView>
        </Box>
      )}

      {/* Create Type Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Type</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField
              autoFocus
              label="Type Name"
              fullWidth
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="e.g., project, task, user"
              helperText="Enter a unique type name (lowercase, underscores allowed)"
              disabled={creating}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newTypeName.trim()) {
                  handleCreateType();
                }
              }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newTypeDescription}
              onChange={(e) => setNewTypeDescription(e.target.value)}
              placeholder="Optional description for this type"
              disabled={creating}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateType}
            variant="contained"
            disabled={!newTypeName.trim() || creating}
            startIcon={creating ? <CircularProgress size={16} /> : <Add />}
          >
            {creating ? 'Creating...' : 'Create Type'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
